(() => {
  const FINGERS = [
    { key: 'thumb', label: 'Thumb', dip: 3, tip: 4 },
    { key: 'index', label: 'Index', dip: 7, tip: 8 },
    { key: 'middle', label: 'Middle', dip: 11, tip: 12 },
    { key: 'ring', label: 'Ring', dip: 15, tip: 16 },
    { key: 'pinky', label: 'Pinky', dip: 19, tip: 20 },
  ];

  const CARD_WIDTH_MM = 85.6;
  const CARD_HEIGHT_MM = 53.98;
  const SIZE_CHART = [
    { name: 'XS', label: 'Extra Small', widths: [14, 10, 11, 10, 8] },
    { name: 'S', label: 'Small', widths: [15, 11, 12, 11, 8] },
    { name: 'M', label: 'Medium', widths: [16, 12, 13, 12, 9] },
    { name: 'L', label: 'Large', widths: [16, 13, 14, 13, 10] },
  ];

  const MEDIAPIPE_VERSION = '0.10.14';
  const VISION_MODULE_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/vision_bundle.mjs`;
  const VISION_WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_VERSION}/wasm`;
  const HAND_MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';
  const CAMERA_CONSTRAINT_ATTEMPTS = [
    { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 960 } } },
    { video: true },
  ];

  let scrollLockCount = 0;
  let handLandmarkerPromise = null;

  function lockBodyScroll() {
    scrollLockCount += 1;
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.documentElement.style.overflow = '';
  }

  async function getCameraPermissionState() {
    try {
      if (!navigator.permissions?.query) return 'unsupported';
      return (await navigator.permissions.query({ name: 'camera' })).state;
    } catch {
      return 'unsupported';
    }
  }

  function loadHandLandmarker() {
    if (handLandmarkerPromise) return handLandmarkerPromise;
    handLandmarkerPromise = (async () => {
      const { HandLandmarker, FilesetResolver } = await import(VISION_MODULE_URL);
      const vision = await FilesetResolver.forVisionTasks(VISION_WASM_BASE);
      return HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: HAND_MODEL_URL, delegate: 'GPU' },
        runningMode: 'IMAGE',
        numHands: 1,
      });
    })().catch((error) => {
      handLandmarkerPromise = null;
      throw error;
    });
    return handLandmarkerPromise;
  }

  function isSkinPixel(data, width, height, x, y) {
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    const index = (y * width + x) * 4;
    const red = data[index];
    const green = data[index + 1];
    const blue = data[index + 2];
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    return red > 55 && green > 30 && blue > 15 && max - min > 12 && Math.abs(red - green) > 8 && red > green && red >= blue - 4;
  }

  function scanEdge(data, width, height, startX, startY, dirX, dirY, maxDistance) {
    let lastGood = 0;
    for (let distance = 0; distance <= maxDistance; distance += 1) {
      const x = Math.round(startX + dirX * distance);
      const y = Math.round(startY + dirY * distance);
      if (!isSkinPixel(data, width, height, x, y)) break;
      lastGood = distance;
    }
    return lastGood;
  }

  function measureFingerWidthPx(landmarks, finger, canvasWidth, canvasHeight, imageData) {
    const dip = landmarks[finger.dip];
    const tip = landmarks[finger.tip];
    const dipPx = { x: dip.x * canvasWidth, y: dip.y * canvasHeight };
    const tipPx = { x: tip.x * canvasWidth, y: tip.y * canvasHeight };
    const dx = tipPx.x - dipPx.x;
    const dy = tipPx.y - dipPx.y;
    const segmentLength = Math.hypot(dx, dy);
    if (segmentLength < 2) return null;

    const directionX = dx / segmentLength;
    const perpendicularX = -(dy / segmentLength);
    const perpendicularY = directionX;
    const samplePoint = 0.6;
    const sampleX = dipPx.x + dx * samplePoint;
    const sampleY = dipPx.y + dy * samplePoint;
    const maxScan = Math.max(segmentLength * 1.1, 8);
    const left = scanEdge(imageData.data, canvasWidth, canvasHeight, sampleX, sampleY, -perpendicularX, -perpendicularY, maxScan);
    const right = scanEdge(imageData.data, canvasWidth, canvasHeight, sampleX, sampleY, perpendicularX, perpendicularY, maxScan);
    const widthPx = left + right;
    return widthPx > 3 ? widthPx : null;
  }

  function matchSize(widthsMm) {
    return SIZE_CHART.find((size) => widthsMm.every((width, index) => width <= size.widths[index])) || SIZE_CHART[SIZE_CHART.length - 1];
  }

  function luminance(data, width, height, x, y) {
    const safeX = Math.max(0, Math.min(width - 1, Math.round(x)));
    const safeY = Math.max(0, Math.min(height - 1, Math.round(y)));
    const index = (safeY * width + safeX) * 4;
    return data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114;
  }

  function edgeContrast(imageData, start, end, normal, offset) {
    let total = 0;
    let samples = 0;
    for (let step = 0.12; step <= 0.88; step += 0.08) {
      const x = start.x + (end.x - start.x) * step;
      const y = start.y + (end.y - start.y) * step;
      const inside = luminance(imageData.data, imageData.width, imageData.height, x + normal.x * offset, y + normal.y * offset);
      const outside = luminance(imageData.data, imageData.width, imageData.height, x - normal.x * offset, y - normal.y * offset);
      total += Math.abs(inside - outside);
      samples += 1;
    }
    return samples ? total / samples : 0;
  }

  class YnCameraSizeFinder extends HTMLElement {
    connectedCallback() {
      this.dialog = this.dialog || this.querySelector('[data-yn-csf-dialog]');
      this.trigger = this.trigger || this.querySelector('[data-yn-csf-open]');
      if (this.dialog && this.dialog.parentElement !== document.body) document.body.appendChild(this.dialog);
      if (this.dataset.ynCsfReady === 'true') return;

      this.dataset.ynCsfReady = 'true';
      this.stream = null;
      this.rafId = null;
      this.measurements = null;
      this.mmPerPixel = null;
      this.cardReady = false;
      this.cardConfidence = 0;
      this.lastCardCheck = 0;
      this.videoEl = document.createElement('video');
      this.videoEl.setAttribute('playsinline', '');
      this.videoEl.setAttribute('autoplay', '');
      this.videoEl.className = 'yn-csf__video--offscreen';
      this.videoEl.muted = true;
      this.dialog?.querySelector('.yn-csf__inner')?.appendChild(this.videoEl);

      this.updateInstructions();
      this.trigger?.addEventListener('click', () => this.open());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target.closest('[data-yn-csf-close]')) return this.close();
        if (event.target.closest('[data-yn-csf-manual]')) {
          this.close();
          this.dispatchEvent(new CustomEvent('yn-size-manual-requested', { bubbles: true }));
          return;
        }
        if (event.target.closest('[data-yn-csf-start]')) return this.handleStart();
        if (event.target.closest('[data-yn-csf-capture]')) return this.capturePhoto();
        if (event.target.closest('[data-yn-csf-retry]')) {
          this.showScreen('intro');
          this.checkCameraPermissionAndAdvance();
          return;
        }
        if (event.target.closest('[data-yn-csf-apply]')) this.applyMeasurements();
      });
      this.dialog?.addEventListener('cancel', () => this.teardownCamera());
      this.dialog?.addEventListener('close', () => {
        this.teardownCamera();
        unlockBodyScroll();
      });
    }

    updateInstructions() {
      const intro = this.dialog?.querySelector('[data-yn-csf-screen="intro"]');
      const heading = intro?.querySelector('h3');
      const paragraph = intro?.querySelector('p:not(.yn-csf__eyebrow)');
      const list = intro?.querySelector('.yn-csf__list');
      const scanHeading = this.dialog?.querySelector('[data-yn-csf-screen="scan"] h3');
      const guideLabel = this.dialog?.querySelector('[data-yn-csf-card-guide] span');
      const captureButton = this.dialog?.querySelector('[data-yn-csf-capture]');

      if (heading) heading.textContent = 'One photo. Your best fit.';
      if (paragraph) paragraph.textContent = 'Use any bank, debit, or ID card as a size reference. Your photo is processed on this device and is not saved.';
      if (list) {
        list.innerHTML = '<li>Place the card flat on a solid, well-lit surface</li><li>Align it with the card frame until the border turns green</li><li>Keep the card still, place one hand flat beside it, then tap Take photo</li>';
      }
      if (scanHeading) scanHeading.textContent = 'Align the card, then add your hand';
      if (guideLabel) guideLabel.textContent = 'Align card inside frame';
      if (captureButton) {
        captureButton.textContent = 'Take photo';
        captureButton.disabled = true;
      }
      this.dialog?.querySelector('[data-yn-csf-hand-guide]')?.setAttribute('hidden', '');
    }

    open() {
      this.showScreen('intro');
      this.dialog?.showModal();
      lockBodyScroll();
      this.checkCameraPermissionAndAdvance();
    }

    async checkCameraPermissionAndAdvance() {
      const permission = await getCameraPermissionState();
      if (!this.dialog?.open) return;
      if (permission === 'granted') this.handleStart();
      if (permission === 'denied') this.showError(this.cameraErrorMessage({ name: 'NotAllowedError' }));
    }

    close() {
      this.dialog?.close();
    }

    handleStart() {
      this.beginScan();
    }

    showScreen(name) {
      this.dialog?.querySelectorAll('[data-yn-csf-screen]').forEach((screen) => {
        screen.hidden = screen.dataset.ynCsfScreen !== name;
      });
    }

    setStatus(message) {
      const status = this.dialog?.querySelector('[data-yn-csf-status]');
      if (status) status.textContent = message || '';
    }

    async beginScan() {
      this.mmPerPixel = null;
      this.cardReady = false;
      this.cardConfidence = 0;
      this.showScreen('scan');
      this.setCardReady(false);

      const spinner = this.dialog?.querySelector('[data-yn-csf-spinner]');
      if (spinner) spinner.hidden = false;
      this.setStatus('Starting camera…');

      if (!navigator.mediaDevices?.getUserMedia) {
        this.showError('This browser does not support camera access here. Please measure manually instead.');
        return;
      }

      let lastError = null;
      let attemptIndex = -1;
      for (let index = 0; index < CAMERA_CONSTRAINT_ATTEMPTS.length; index += 1) {
        try {
          this.stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINT_ATTEMPTS[index]);
          attemptIndex = index;
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!this.stream) {
        this.showError(this.cameraErrorMessage(lastError));
        return;
      }

      const reportedFacing = this.stream.getVideoTracks()[0]?.getSettings?.().facingMode;
      const isFrontFacing = reportedFacing ? reportedFacing === 'user' : attemptIndex !== 0;
      if (this.dialog) this.dialog.dataset.ynCsfFacing = isFrontFacing ? 'user' : 'environment';
      this.videoEl.srcObject = this.stream;
      try {
        await this.videoEl.play();
      } catch {}
      await new Promise((resolve) => {
        if (this.videoEl.videoWidth) return resolve();
        this.videoEl.addEventListener('loadedmetadata', resolve, { once: true });
      });

      const canvas = this.dialog.querySelector('[data-yn-csf-canvas="scan"]');
      canvas.width = this.videoEl.videoWidth;
      canvas.height = this.videoEl.videoHeight;
      if (spinner) spinner.hidden = true;
      this.setStatus('Align the card with the frame. The border will turn green when it is ready.');
      this.runDrawLoop(canvas);
      loadHandLandmarker().catch(() => {});
    }

    guideGeometry(canvas) {
      const stage = this.dialog?.querySelector('.yn-csf__stage');
      const guide = this.dialog?.querySelector('[data-yn-csf-card-guide]');
      if (!stage || !guide || !canvas.width || !canvas.height) return null;

      const stageRect = stage.getBoundingClientRect();
      const guideRect = guide.getBoundingClientRect();
      const scale = Math.max(stageRect.width / canvas.width, stageRect.height / canvas.height);
      if (!Number.isFinite(scale) || scale <= 0) return null;

      const renderedWidth = canvas.width * scale;
      const renderedHeight = canvas.height * scale;
      const cropX = (renderedWidth - stageRect.width) / 2;
      const cropY = (renderedHeight - stageRect.height) / 2;
      const left = (guideRect.left - stageRect.left + cropX) / scale;
      const top = (guideRect.top - stageRect.top + cropY) / scale;
      const width = guideRect.width / scale;
      const height = guideRect.height / scale;
      return { left, top, right: left + width, bottom: top + height, width, height, scale };
    }

    cardAlignmentScore(canvas) {
      const geometry = this.guideGeometry(canvas);
      if (!geometry) return { aligned: false, geometry: null };
      const context = canvas.getContext('2d', { willReadFrequently: true });
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const offset = Math.max(3, Math.round(5 / geometry.scale));
      const topLeft = { x: geometry.left, y: geometry.top };
      const topRight = { x: geometry.right, y: geometry.top };
      const bottomLeft = { x: geometry.left, y: geometry.bottom };
      const bottomRight = { x: geometry.right, y: geometry.bottom };
      const scores = [
        edgeContrast(imageData, topLeft, topRight, { x: 0, y: 1 }, offset),
        edgeContrast(imageData, bottomLeft, bottomRight, { x: 0, y: -1 }, offset),
        edgeContrast(imageData, topLeft, bottomLeft, { x: 1, y: 0 }, offset),
        edgeContrast(imageData, topRight, bottomRight, { x: -1, y: 0 }, offset),
      ];
      const strongEdges = scores.filter((score) => score >= 11).length;
      const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      return { aligned: strongEdges >= 3 && average >= 13, geometry };
    }

    setCardReady(ready) {
      this.cardReady = ready;
      const guide = this.dialog?.querySelector('[data-yn-csf-card-guide]');
      const button = this.dialog?.querySelector('[data-yn-csf-capture]');
      guide?.classList.toggle('is-card-ready', ready);
      if (guide) guide.setAttribute('aria-label', ready ? 'Card aligned correctly' : 'Align card inside frame');
      if (button) button.disabled = !ready;
      if (ready) {
        this.setStatus('Card aligned. Keep it still, place your hand flat beside it, then tap Take photo.');
      } else {
        this.setStatus('Align the card with the frame. The border will turn green when it is ready.');
      }
    }

    checkCardAlignment(canvas, timestamp) {
      if (timestamp - this.lastCardCheck < 180) return;
      this.lastCardCheck = timestamp;
      let result;
      try {
        result = this.cardAlignmentScore(canvas);
      } catch {
        return;
      }
      this.cardConfidence = Math.max(0, Math.min(5, this.cardConfidence + (result.aligned ? 1 : -1)));
      const ready = this.cardConfidence >= 3;
      if (ready !== this.cardReady) this.setCardReady(ready);
    }

    runDrawLoop(canvas) {
      const context = canvas.getContext('2d');
      const draw = (timestamp) => {
        context.drawImage(this.videoEl, 0, 0, canvas.width, canvas.height);
        this.checkCardAlignment(canvas, timestamp);
        this.rafId = requestAnimationFrame(draw);
      };
      this.rafId = requestAnimationFrame(draw);
    }

    stopDrawLoop() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    async capturePhoto() {
      if (!this.cardReady) {
        this.setStatus('Keep aligning the card until the border turns green.');
        return;
      }

      const canvas = this.dialog?.querySelector('[data-yn-csf-canvas="scan"]');
      const geometry = this.guideGeometry(canvas);
      if (!canvas || !geometry) {
        this.showError('Something went wrong reading the camera. Please try again.');
        return;
      }

      this.stopDrawLoop();
      const fromWidth = CARD_WIDTH_MM / geometry.width;
      const fromHeight = CARD_HEIGHT_MM / geometry.height;
      this.mmPerPixel = (fromWidth + fromHeight) / 2;
      this.setStatus('Reading your hand…');

      let landmarker;
      try {
        landmarker = await loadHandLandmarker();
      } catch {
        this.showError('We could not load the hand-scanning model. Please try again, or measure manually.');
        return;
      }

      let detection;
      try {
        detection = landmarker.detect(canvas);
      } catch {
        this.showError('We had trouble reading that photo. Please try again with steadier lighting.');
        return;
      }

      const landmarks = detection?.landmarks?.[0];
      if (!landmarks) {
        this.showError('We could not clearly see your hand. Keep the card inside the frame and place your hand flat beside it with fingers spread.');
        return;
      }

      const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
      const widthsMm = FINGERS.map((finger) => measureFingerWidthPx(landmarks, finger, canvas.width, canvas.height, imageData)).map((pixels) =>
        pixels == null ? null : Math.round(pixels * this.mmPerPixel * 10) / 10,
      );

      if (widthsMm.filter((width) => width == null).length > 1) {
        this.showError('We could not measure enough fingers clearly. Keep your hand flat beside the card, spread your fingers, and use bright even light.');
        return;
      }

      const perFinger = {};
      widthsMm.forEach((width, index) => {
        if (width == null) {
          const neighbours = [widthsMm[index - 1], widthsMm[index + 1]].filter((value) => value != null);
          widthsMm[index] = neighbours.length
            ? Math.round((neighbours.reduce((sum, value) => sum + value, 0) / neighbours.length) * 10) / 10
            : SIZE_CHART[1].widths[index];
        }
        perFinger[FINGERS[index].key] = widthsMm[index];
      });

      this.measurements = perFinger;
      this.teardownCamera();
      this.showResult(widthsMm);
    }

    showResult(widthsMm) {
      const size = matchSize(widthsMm);
      this.showScreen('result');
      const rows = this.dialog?.querySelector('[data-yn-csf-result-rows]');
      if (rows) rows.innerHTML = FINGERS.map((finger, index) => `<tr><th>${finger.label}</th><td>${widthsMm[index]} mm</td></tr>`).join('');
      const heading = this.dialog?.querySelector('[data-yn-csf-result-size]');
      if (heading) heading.textContent = `${size.label} (${size.name})`;
      const applyButton = this.dialog?.querySelector('[data-yn-csf-apply]');
      if (applyButton) applyButton.dataset.size = size.name;
    }

    applyMeasurements() {
      const size = this.dialog?.querySelector('[data-yn-csf-apply]')?.dataset.size;
      this.dispatchEvent(new CustomEvent('yn-size-scanned', { bubbles: true, detail: { measurements: this.measurements, size } }));
      this.close();
    }

    showError(message) {
      this.teardownCamera();
      this.showScreen('error');
      const element = this.dialog?.querySelector('[data-yn-csf-error-message]');
      if (element) element.textContent = message;
    }

    cameraErrorMessage(error) {
      if (error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
        return 'Camera access is turned off for this site. Tap the camera icon or padlock in your browser, allow access, then tap Try again — or measure manually instead.';
      }
      if (error?.name === 'NotFoundError') return 'No camera was found on this device. You can still measure manually with a tape or ruler.';
      if (error?.name === 'NotReadableError') return 'Another app or tab may already be using your camera. Close it and try again, or measure manually.';
      return 'We could not start the camera. Please try again, or measure manually instead.';
    }

    teardownCamera() {
      this.stopDrawLoop();
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    }

    disconnectedCallback() {
      this.teardownCamera();
    }
  }

  if (!customElements.get('yn-camera-size-finder')) {
    customElements.define('yn-camera-size-finder', YnCameraSizeFinder);
  }
})();
